// Project detail at ≤880px (mobile overhaul Phase B §6, mockup §6.1–§6.3).
// The densest page in the app read as a brief: header, stepper, one primary
// action, tasks, notes, the six rail groups as accordions with a one-line
// summary each, timeline, stamp. The desktop two-column `.detail-grid` and
// `ProjectRailBody` are not rendered at this width.
//
// It owns no data: pages/ProjectDetail.tsx keeps every fetch, handler and
// modal and hands them down, so both renders always PATCH the same way. The
// pure helpers both sides need live in lib/projectDetailShared.ts — importing
// them from the page would make an ESM cycle.
//
// Nothing here is gesture-only (decision 6): every action is in the "···"
// sheet or on a visible row. Every inline field opens a FieldSheet /
// DateSheet / PickerSheet with its own title — no click-to-edit swaps.

import { testimonialMediaAdminLink, testimonialPermissionLabel } from '../../../shared/testimonial';
import { DeliverablesPanel } from '../DeliverablesPanel';
import { WrapNotesCard } from '../WrapNotesCard';
import { ProjectMeetings } from '../ProjectMeetings';
import { DrivePicker } from '../DrivePicker';
import { ReviewArtifactActions } from '../ReviewArtifactActions';
import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { calendarDaysBetween, melbourneDate } from '../../../shared/dates';
import { bookingCostCents } from '../../../shared/crewCost';
import {
  PHASE_LABELS,
  PROJECT_PHASES,
  SOCIAL_STATUSES,
  SOCIAL_STATUS_LABELS,
  type Brand,
  type SocialStatus,
  type ProjectPhase,
} from '../../../shared/types';
import { formatMoney, parseMoney } from '../../lib/moneyFormat';
import { formatDate, formatDayMonth, formatProjectNumber } from '../../lib/calendar';
import { formatInvoiceNumber } from '../../../shared/format';
import {
  artifactStatusLabel,
  artifactStatusTone,
  contactOptionLabel,
  CREW_COST_ERROR_TITLE,
  crewCalcLabel,
  crewDateLabel,
  crewFooterSub,
  CUSTOM_CONTACT_VALUE,
  DASHBOARD_BASE,
  invoiceOverdue,
  PHASE_DOTS,
  phaseMoveArrow,
  phaseMoveConsequence,
  pillTone,
  socialTone,
  STEP_LABELS,
  STEPS,
  TOOL_MAP,
  waitingNextStatus,
} from '../../lib/projectDetailShared';
import {
  automationsSummary,
  contactSummary,
  crewSummary,
  moneySummary,
  statusDatesSummary,
  toolsSummary,
} from '../../lib/projectDetailSummaries';
import { ProjectMoneyRows, ProjectMoneyStatus } from '../ProjectMoneyRows';
import { AccordionGroup } from '../AccordionGroup';
import { ArtifactTitle } from '../ArtifactTitle';
import { artifactTypeLabel } from '../../../shared/artifactTypes';
import { DateSheet } from '../DateSheet';
import { FieldRow, FieldSheet } from '../FieldSheet';
import {
  BackIcon,
  CheckIcon,
  DotsIcon,
  LinkIcon,
  LostIcon,
  MDashboardsIcon,
  MNoteIcon,
  MScriptsIcon,
  MTasksIcon,
  PlusIcon,
  TrashIcon,
  XIcon,
} from '../icons';
import { PageMenu, type PageMenuItem } from '../PageMenu';
import { PickerSheet } from '../PickerSheet';
import { Group, NotesBlock, Pill, Seg, Timeline, TimelineRow } from '../primitives';
import { Sheet, SheetButton, SheetRow, SheetSection } from '../Sheet';
import { QuickAddRow, TaskRow } from '../TaskRow';
import { ProjectDetailBookingSheet } from '../projectDetailBookingSheet';
import type { ProjectDetailMobileProps, FieldEdit, DateEdit, Sub } from './types';
import { SummaryLine, centsDraft, gateLabel, testimonialSub } from './helpers';
import { RangeSheet } from './RangeSheet';
import { ContactRows } from './ContactRows';

export function ProjectDetailMobile(props: ProjectDetailMobileProps) {
  const {
    project,
    today,
    mode,
    dashboardSlug,
    moneyTotal,
    stepDate,
    subBits,
    openTasks,
    doneTasks,
    showDone,
    setShowDone,
    addSignal,
    templates,
    applyTemplate,
    onNewTask,
    onSaveTemplate,
    activity,
    wrapNotes,
    tlDate,
    invoices,
    clients,
    ensureClients,
    clientContacts,
    resolvedContact,
    crewBookings,
    crewRegister,
    crewConflicts,
    crewTotalCents,
    crewPeopleCount,
    crewDayCount,
    crewHalfCount,
    artifacts,
    onReviewArtifactUpdated,
    unlinked,
    toolIntegrations,
    deliverables,
    deliveryLinkBusy,
    deliveryLinkNote,
    waitingBusy,
    waitingNote,
    refresh,
    patch,
    waitingSendNow,
    waitingSnooze,
    testimonialAction,
    patchCrewBooking,
    removeCrewBooking,
    addCrewBooking,
    copyDeliveryLink,
    unlinkArtifact,
    adoptArtifact,
    onCreateArtifact,
    onCutApproved,
    onCloseProject,
    onVoid,
    onChangeNumber,
    onTrashProject,
  } = props;

  const [menuOpen, setMenuOpen] = useState(false);
  const [sub, setSub] = useState<Sub | null>(null);
  const [fieldEdit, setFieldEdit] = useState<FieldEdit | null>(null);
  const [dateEdit, setDateEdit] = useState<DateEdit | null>(null);
  // Mirrors ProjectRailBody: "Custom…" is a transient mode until one of the
  // three override fields actually holds a value.
  const [transientCustomContact, setTransientCustomContact] = useState(false);
  useEffect(() => setTransientCustomContact(false), [project.id]);

  const voided = project.voided_at !== null;
  const stepIndex = STEPS.indexOf(project.phase);
  const numberLabel = formatProjectNumber(project.project_number);
  const quoteCents = project.value_cents ?? moneyTotal;

  const closeSub = () => setSub(null);
  // Closing from Delivered/Clean up goes straight to the close sheet — it is
  // its own confirmation, so skip the generic "Move to Closed?" step.
  const pickPhase = (phase: ProjectPhase) => {
    if (phase === 'closed' && (project.phase === 'delivered' || project.phase === 'clean_up')) {
      closeSub();
      onCloseProject();
    } else {
      setSub({ kind: 'phase-confirm', phase });
    }
  };
  const openField = (edit: FieldEdit) => setFieldEdit(edit);
  const openDate = (edit: DateEdit) => setDateEdit(edit);

  // ── Summaries ──────────────────────────────────────────────────────────
  const linkedTools = TOOL_MAP.flatMap(({ tool, label }) => {
    const row = artifacts.find((a) => a.tool === tool);
    return row ? [{ label, status: row.status ? artifactStatusLabel(row.status) : null }] : [];
  });
  const waitingDays =
    project.waiting_on_client_since === null
      ? null
      : Math.max(0, calendarDaysBetween(melbourneDate(project.waiting_on_client_since), today));

  // ── Dates ──────────────────────────────────────────────────────────────
  const rangeValue = (start: string | null, end: string | null): ReactNode => {
    if (!start) return '';
    if (!end || end === start) return formatDate(start);
    return `${formatDayMonth(start)} → ${formatDayMonth(end)}`;
  };

  // ── Tools ──────────────────────────────────────────────────────────────
  const toolConfigured = (tool: string) =>
    ({
      script: toolIntegrations.scripts,
      callsheet: toolIntegrations.callsheets,
      proposal: false,
      review: toolIntegrations.reviews,
    })[tool] ?? false;

  const openToolUrlSheet = (label: string, urlField: 'script_url' | 'callsheet_url' | 'proposal_url') =>
    openField({
      title: `${label} URL`,
      context: project.name,
      kind: 'url',
      value: project[urlField] ?? '',
      placeholder: 'https://…',
      onSave: (raw) => void patch({ [urlField]: raw.trim() || null }),
    });

  // ── Page menu (mockup §6.1 C) — every page and rail action lives here ──
  const menuItems: PageMenuItem[] = [
    { key: 'new-task', icon: <PlusIcon />, label: 'New task', onSelect: onNewTask },
    ...(templates.length > 0
      ? [
          {
            key: 'from-template',
            icon: <MTasksIcon />,
            label: 'Add tasks from template',
            onSelect: () => setSub({ kind: 'template' }),
          },
        ]
      : []),
    ...(openTasks.length + doneTasks.length > 0
      ? [
          {
            key: 'save-template',
            icon: <MNoteIcon />,
            label: 'Save tasks as template',
            onSelect: onSaveTemplate,
          },
        ]
      : []),
    {
      key: 'delivery-link',
      icon: <LinkIcon />,
      label: 'Copy delivery link',
      disabled: deliveryLinkBusy,
      onSelect: () => void copyDeliveryLink(),
    },
    ...(dashboardSlug
      ? [
          {
            key: 'dashboard',
            icon: <MDashboardsIcon />,
            label: 'Client dashboard ↗',
            onSelect: () => window.open(`${DASHBOARD_BASE}/${dashboardSlug}`, '_blank', 'noreferrer'),
          },
        ]
      : []),
    {
      key: 'number',
      icon: <MScriptsIcon />,
      label: 'Change project number',
      sublabel: 'Renumbers linked invoices with confirmation',
      onSelect: onChangeNumber,
    },
    {
      key: 'void',
      icon: <LostIcon />,
      label: voided ? 'Unvoid project' : 'Void project',
      onSelect: onVoid,
    },
    {
      key: 'trash',
      icon: <TrashIcon />,
      label: 'Move project to trash',
      tone: 'danger',
      onSelect: () => setSub({ kind: 'trash' }),
    },
  ];

  return (
    <div className="page-body">
      <div className="pd-m">
        {/* ── Header (mockup §6.1 A) ─────────────────────────────────── */}
        <div className="pd-head">
          <div className="pd-head-row">
            <Link className="pd-back" to="/projects">
              <BackIcon />
              Projects
            </Link>
            <button
              type="button"
              className={`ib${menuOpen ? ' on' : ''}`}
              aria-label="Project actions"
              onClick={() => setMenuOpen(true)}
            >
              <DotsIcon />
            </button>
          </div>
          <h1 className="pd-title">
            {numberLabel && <span className="num">{numberLabel}</span>}
            {project.name}
            {voided && (
              <span className="pd-title-pill">
                <Pill tone="danger">Voided</Pill>
              </span>
            )}
          </h1>
          <div className="pd-sub">{subBits.join(' · ')}</div>
        </div>

        {/* ── Stepper (decision 8: a tap confirms first) ─────────────── */}
        <div className="stepper pd-stepper">
          {STEPS.map((phase, i) => {
            const cls = stepIndex >= 0 && i < stepIndex ? ' done' : phase === project.phase ? ' current' : '';
            return (
              <button
                type="button"
                key={phase}
                className={`step${cls}${phase === 'clean_up' ? ' step-cleanup' : ''}`}
                aria-current={phase === project.phase ? 'step' : undefined}
                onClick={() => phase !== project.phase && pickPhase(phase)}
              >
                <span className="step-dot" />
                <span className="step-label">{STEP_LABELS[phase]}</span>
                <span className="step-date">{stepDate(phase)}</span>
              </button>
            );
          })}
        </div>

        {/* ── Primary action (mockup §6.1 A) ─────────────────────────── */}
        {(project.phase !== 'closed' || dashboardSlug) && (
          <div className="pd-primary">
            {project.phase === 'post' ? (
              <button
                type="button"
                className="cbtn wide pri"
                disabled={voided}
                onClick={onCutApproved}
              >
                Cut approved →
              </button>
            ) : project.phase === 'delivered' ? (
              <button
                type="button"
                className="cbtn wide pri"
                onClick={() => void patch({ phase: 'clean_up' })}
              >
                Move to clean up →
              </button>
            ) : project.phase === 'clean_up' ? (
              <button type="button" className="cbtn wide pri" onClick={onCloseProject}>
                Close project →
              </button>
            ) : (
              project.phase !== 'closed' && (
                <button
                  type="button"
                  className="cbtn wide pri"
                  onClick={() => void patch({ phase: 'delivered' })}
                >
                  Mark delivered →
                </button>
              )
            )}
            {dashboardSlug && (
              <a
                className="cbtn pd-dash"
                href={`${DASHBOARD_BASE}/${dashboardSlug}`}
                target="_blank"
                rel="noreferrer"
              >
                Dashboard ↗
              </a>
            )}
          </div>
        )}
        {voided && project.phase === 'post' && (
          <div className="pd-hint">Unvoid the project first — a voided project fires no automation.</div>
        )}

        {/* ── Tasks ──────────────────────────────────────────────────── */}
        <Group
          title="Tasks"
          count={`${openTasks.length} open`}
          link={
            doneTasks.length > 0 && (
              <button type="button" className="group-link pd-glink" onClick={() => setShowDone(!showDone)}>
                {showDone ? 'Hide done' : 'Show done →'}
              </button>
            )
          }
        >
          {openTasks.map((t) => (
            <TaskRow key={t.id} task={t} today={today} projectDisplay="none" onChanged={refresh} />
          ))}
          {openTasks.length === 0 && !showDone && <div className="pd-empty">No open tasks.</div>}
          {showDone &&
            doneTasks.map((t) => (
              <div key={t.id} className="pd-task-done">
                <TaskRow task={t} today={today} projectDisplay="none" onChanged={refresh} />
              </div>
            ))}
          <QuickAddRow
            hint="Add task to this project…"
            defaults={{ project_id: project.id, due_date: today }}
            onAdded={refresh}
            openSignal={addSignal}
          />
        </Group>

        <WrapNotesCard notes={wrapNotes} mobile />

        <DeliverablesPanel projectNumber={project.project_number} projectName={project.name} key={project.id} projectId={project.id} deliverables={deliverables} artifacts={artifacts} onChanged={refresh} copyDeliveryLink={copyDeliveryLink} deliveryLinkBusy={deliveryLinkBusy} deliveryLinkNote={deliveryLinkNote} mobile />

        <ProjectMeetings key={`meetings-${project.id}`} projectId={project.id} mobile />

        {/* ── Notes ──────────────────────────────────────────────────── */}
        <NotesBlock notes={project.summary} onSave={(summary) => void patch({ summary })} />

        {/* ── The six rail groups as accordions (mockup §6.1 B) ──────── */}
        <div className="pd-groups">
          <AccordionGroup
            slug="status-dates"
            title="Status & dates"
            summary={statusDatesSummary({
              phaseLabel: PHASE_LABELS[project.phase],
              brand: project.brand,
              shootDate: project.shoot_date,
              shootEndDate: project.shoot_end_date,
              editDate: project.edit_date,
              editEndDate: project.edit_end_date,
              dueDate: project.due_date,
            })}
          >
            <FieldRow
              label="Phase"
              value={<Pill tone={pillTone(project.phase)}>{PHASE_LABELS[project.phase]}</Pill>}
              onPress={() => setSub({ kind: 'phase-pick' })}
            />
            <div className="fieldrow pd-segrow">
              <span className="fieldrow-k">Brand</span>
              <Seg<Brand>
                options={[
                  { value: 'hsp', label: 'HSP' },
                  { value: 'ss', label: 'SS' },
                ]}
                value={project.brand}
                onChange={(brand) => void patch({ brand })}
              />
            </div>
            <FieldRow
              label="Due"
              mono
              value={formatDate(project.due_date)}
              empty={!project.due_date}
              onPress={() =>
                openDate({
                  title: 'Due date',
                  context: project.name,
                  value: project.due_date,
                  allowClear: true,
                  onPick: (due_date) => void patch({ due_date }),
                })
              }
            />
            <FieldRow
              label="Shoot"
              mono
              value={rangeValue(project.shoot_date, project.shoot_end_date)}
              empty={!project.shoot_date}
              onPress={() => setSub({ kind: 'range', which: 'shoot' })}
            />
            <FieldRow
              label="Edit"
              mono
              value={rangeValue(project.edit_date, project.edit_end_date)}
              empty={!project.edit_date}
              onPress={() => setSub({ kind: 'range', which: 'edit' })}
            />
          </AccordionGroup>

          <AccordionGroup slug="money" title="Money" badge={mode === 'incl' ? 'inc GST' : 'ex GST'}
            status={<ProjectMoneyStatus status={props.invoiceDraft?.strip.quote_status ?? project.quote_status} />}
            summary={<SummaryLine parts={moneySummary({ quoteCents, invoiceCount: invoices.length,
              overdueCount: invoices.filter((inv) => invoiceOverdue(inv, today)).length })} />}>
            <ProjectMoneyRows mobile draft={props.invoiceDraft} invoices={invoices} today={today} mode={mode} quoteStatus={project.quote_status}
              quote={<button type="button" className="mr-quote-edit" onClick={() => openField({
                title: 'Quote', context: 'ex GST', kind: 'money', value: centsDraft(quoteCents),
                onSave: (raw) => void patch({ value_cents: parseMoney(raw) }),
              })}>{formatMoney(quoteCents)}</button>}
              action={props.invoiceAction} crew={crewBookings.length > 0 && <>
                <div className="mr-foot"><span>Crew cost</span><span>{formatMoney(crewTotalCents)}</span></div>
                {crewTotalCents === null && <div className="pd-warn">{CREW_COST_ERROR_TITLE}</div>}
              </>} />
          </AccordionGroup>

          <AccordionGroup
            slug="contact"
            title="Contact"
            summary={contactSummary({
              contactName: resolvedContact.name,
              clientName: project.client_name,
            })}
          >
            <div className="fieldrow pd-clientrow">
              <span className="fieldrow-k">Client</span>
              <button
                type="button"
                className="frow-tap acc"
                onClick={() => {
                  ensureClients();
                  setSub({ kind: 'client' });
                }}
              >
                <span className="frow-tap-v">{project.client_name}</span>
                <span className="chev">›</span>
              </button>
              <Link
                className="pd-ext"
                to={`/clients/${project.client_id}`}
                aria-label={`Open ${project.client_name}`}
              >
                ↗
              </Link>
            </div>
            <div className="fieldrow">
              <span className="fieldrow-k">Dashboard</span>
              {dashboardSlug ? (
                <a
                  className="fieldrow-v mono acc"
                  href={`${DASHBOARD_BASE}/${dashboardSlug}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  /{dashboardSlug} ↗
                </a>
              ) : (
                <span className="fieldrow-v empty">— none</span>
              )}
            </div>
            <ContactRows
              project={project}
              clientContacts={clientContacts}
              resolvedContact={resolvedContact}
              transientCustom={transientCustomContact}
              setTransientCustom={setTransientCustomContact}
              openField={openField}
              openPicker={() => setSub({ kind: 'contact' })}
              patch={patch}
            />
          </AccordionGroup>

          <AccordionGroup
            slug="crew"
            title="Crew"
            badge={`${crewPeopleCount} ${crewPeopleCount === 1 ? 'person' : 'people'}`}
            summary={
              <SummaryLine
                parts={crewSummary({
                  names: [...new Set(crewBookings.map((b) => b.crew_name))],
                  totalCents: crewTotalCents,
                })}
              />
            }
          >
            {crewBookings.map((booking) => {
              const cost = bookingCostCents(booking);
              const conflicts = crewConflicts[booking.id] ?? [];
              return (
                <button
                  type="button"
                  className="row pd-crewrow"
                  key={booking.id}
                  onClick={() => setSub({ kind: 'booking', booking })}
                >
                  <span className="col">
                    <span className="ttl">{booking.crew_name}</span>
                    <span className="sub">
                      {booking.role || '—'}
                      <span className="sep">·</span>
                      {crewDateLabel(booking.start_date, booking.end_date)}
                      <span className="sep">·</span>
                      {formatMoney(booking.day_rate_cents)}/day
                      {booking.half_day_rate_cents !== null &&
                        ` · ${formatMoney(booking.half_day_rate_cents)}/half`}
                    </span>
                    <span className="sub g">{cost === null ? '—' : crewCalcLabel(cost)}</span>
                    {conflicts.map((cf, i) => (
                      <span className="sub pd-conflict" key={i}>
                        <Pill tone="danger">
                          ⚠ double-booked — {cf.project_name}, {crewDateLabel(cf.start_date, cf.end_date)}
                        </Pill>
                      </span>
                    ))}
                  </span>
                  <span className="chev">›</span>
                </button>
              );
            })}
            {crewBookings.length === 0 && <div className="pd-empty">No crew booked yet.</div>}
            <button
              type="button"
              className="mrow pd-addrow"
              onClick={() => setSub({ kind: 'booking', booking: null })}
            >
              <span className="mrow-ico">
                <PlusIcon />
              </span>
              <span className="mrow-l">
                <span className="mrow-label">Add crew</span>
              </span>
            </button>
            {crewBookings.length > 0 && (
              <div className="pd-total">
                <span>
                  Total crew cost · {crewFooterSub(crewPeopleCount, crewDayCount, crewHalfCount)}
                </span>
                <span className="g" title={crewTotalCents === null ? CREW_COST_ERROR_TITLE : undefined}>
                  {formatMoney(crewTotalCents)}
                </span>
              </div>
            )}
          </AccordionGroup>

          <AccordionGroup
            slug="tools-delivery"
            title="Tools & delivery"
            summary={
              <SummaryLine parts={toolsSummary({ tools: linkedTools, deliveryCount: deliverables.length })} />
            }
          >
            {TOOL_MAP.map(({ tool, urlField, route, label }) => {
              const matching = artifacts.filter((artifact) => artifact.tool === tool);
              const rows = tool === 'review' && matching.length ? matching : [matching[0] ?? null];
              const pastedUrl = urlField === null ? null : project[urlField];
              const canCreate = tool === 'script' || tool === 'callsheet' || tool === 'review';
              const configured = toolConfigured(tool);
              return <Fragment key={tool}>{rows.map((row) => {
              const openHref = row?.admin_url
                ? `/tools/${route}?url=${encodeURIComponent(row.admin_url)}`
                : pastedUrl
                  ? `/tools/${route}?url=${encodeURIComponent(pastedUrl)}`
                  : null;
              return (
                <Fragment key={row?.id ?? tool}>
                <div className="pd-tool">
                  <button type="button" className="l" onClick={() => setSub({ kind: 'tool', tool, artifactId: row?.id })}>
                    <span className="ico">
                      <MScriptsIcon />
                    </span>
                    <span className="t">
                      {row ? (
                        <>
                          <ArtifactTitle title={row.title} fallback={label} />
                          <span className="artifact-meta">
                            <span className="art-type">
                              {label}{artifactTypeLabel(row.tool, row.artifact_type) && ` · ${artifactTypeLabel(row.tool, row.artifact_type)}`}
                            </span>
                            {row.status && <Pill tone={artifactStatusTone(row.status)}>{artifactStatusLabel(row.status)}</Pill>}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="n">{label}</span>
                          <small>{pastedUrl ? 'Linked by URL' : 'Not linked'}</small>
                        </>
                      )}
                    </span>
                  </button>
                  {openHref ? (
                    <Link className="act" to={openHref}>
                      Open ↗
                    </Link>
                  ) : row?.public_url ? (
                    <a className="act" href={row.public_url} target="_blank" rel="noreferrer">
                      Open ↗
                    </a>
                  ) : canCreate && configured ? (
                    <button type="button" className="act" onClick={() => onCreateArtifact(tool)}>
                      + New
                    </button>
                  ) : urlField !== null ? (
                    <button type="button" className="act" onClick={() => openToolUrlSheet(label, urlField)}>
                      Paste URL
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="act dim"
                      disabled
                      title={`The ${label.toLowerCase()} tool isn't configured on this worker yet.`}
                    >
                      + New
                    </button>
                  )}
                </div>
                {row?.tool === 'review' && <div style={{ padding: '0 20px 12px' }}><ReviewArtifactActions key={row.id} artifact={row}
                  dashboardSlug={dashboardSlug} configured={toolIntegrations.reviews} mobile onUpdated={onReviewArtifactUpdated} /></div>}
                </Fragment>
              );
              })}</Fragment>;
            })}
            {unlinked.map((candidate, index) => (
              <div className="pd-tool" key={`${candidate.tool}-${candidate.external_id ?? candidate.url ?? index}`}>
                <span className="l">
                  <span className="ico">
                    <MScriptsIcon />
                  </span>
                  <span className="t">
                    <span className="n">{candidate.title || 'Untitled'}</span>
                    <small>Found on the dashboard — link to this project?</small>
                  </span>
                </span>
                <button type="button" className="act" onClick={() => void adoptArtifact(candidate)}>
                  Adopt
                </button>
              </div>
            ))}

            <SheetSection>Delivery page</SheetSection>
            <FieldRow
              label="Gated by"
              value={gateLabel(project.gating_invoice_id, invoices)}
              empty={!project.gating_invoice_id}
              onPress={() => setSub({ kind: 'gate' })}
            />
            <DrivePicker key={project.id} projectId={project.id} />
          </AccordionGroup>

          <AccordionGroup
            slug="automations"
            title="Automations"
            summary={
              <SummaryLine
                parts={automationsSummary({
                  waitingDays,
                  socialLabel: project.social_status ? SOCIAL_STATUS_LABELS[project.social_status] : null,
                  socialRestricted: project.social_restricted === 1,
                })}
              />
            }
          >
            <button
              type="button"
              className="mrow pd-togglerow"
              disabled={waitingBusy}
              aria-pressed={project.waiting_on_client_since !== null}
              onClick={() =>
                void patch({ waiting_on_client: project.waiting_on_client_since === null })
              }
            >
              <span className="mrow-l">
                <span className="mrow-label">Waiting on client</span>
                <small>
                  {project.waiting_on_client_since === null
                    ? 'Flag it while the ball is in their court'
                    : `Since ${formatDayMonth(melbourneDate(project.waiting_on_client_since))} · ${waitingDays}d · Nudge ${project.waiting_nudge_count} of 3 · ${waitingNextStatus(project, today)}`}
                </small>
              </span>
              <span className={`toggle${project.waiting_on_client_since !== null ? ' on' : ''}`} />
            </button>
            {project.waiting_on_client_since !== null && (
              <div className="pd-btnrow">
                <button type="button" className="cbtn acc" disabled={waitingBusy} onClick={() => void waitingSendNow()}>
                  {waitingBusy ? 'Sending…' : 'Send now'}
                </button>
                <button type="button" className="cbtn" disabled={waitingBusy} onClick={() => void waitingSnooze()}>
                  Snooze +1w
                </button>
                {waitingNote && <span className="pd-note">{waitingNote}</span>}
              </div>
            )}
            {project.delivered_date && (
              <SheetRow
                label="Testimonial"
                sub={testimonialSub(project)}
                right="›"
                onSelect={() => setSub({ kind: 'testimonial' })}
              />
            )}
            <div className="fieldrow pd-socialrow">
              <span className="fieldrow-k">Social status</span>
              <button type="button" className="frow-tap" onClick={() => setSub({ kind: 'social' })}>
                {project.social_status ? (
                  <Pill tone={socialTone(project.social_status)}>
                    {SOCIAL_STATUS_LABELS[project.social_status]}
                  </Pill>
                ) : (
                  <span className="frow-tap-v empty">Add</span>
                )}
                <span className="chev">›</span>
              </button>
            </div>
            {/* Project-level social restriction (Social tab, 2026-08): blocks
                every asset under this project. Clearing it is only possible
                here — the assets themselves show it as read-only. */}
            <button
              type="button"
              className="mrow pd-togglerow dng"
              aria-pressed={project.social_restricted === 1}
              onClick={() =>
                void patch(
                  project.social_restricted === 1
                    ? { social_restricted: 0, social_restricted_note: null }
                    : { social_restricted: 1 },
                )
              }
            >
              <span className="mrow-l">
                <span className="mrow-label">No social use</span>
                <small>Flag when the client can&apos;t be posted</small>
              </span>
              <span className={`toggle${project.social_restricted === 1 ? ' on' : ''}`} />
            </button>
            {project.social_restricted === 1 && (
              <FieldRow
                label="Why"
                value={project.social_restricted_note ?? ''}
                empty={!project.social_restricted_note}
                onPress={() =>
                  openField({
                    title: 'Why is this off limits?',
                    context: project.name,
                    kind: 'textarea',
                    value: project.social_restricted_note ?? '',
                    onSave: (raw) => void patch({ social_restricted_note: raw.trim() || null }),
                  })
                }
              />
            )}
          </AccordionGroup>
        </div>

        {/* ── Timeline + stamp ───────────────────────────────────────── */}
        <Group title="Timeline">
          <Timeline>
            {activity.map((a) => (
              <TimelineRow key={a.id} date={tlDate(a.created_at)} callsheetUrl={a.callsheet_url}>
                {a.summary}
              </TimelineRow>
            ))}
            <TimelineRow date={tlDate(project.created_at)}>Project created</TimelineRow>
          </Timeline>
        </Group>
        <div className="pd-stamp">
          <span>Created {formatDate(melbourneDate(project.created_at))}</span>
          <span>Updated {formatDate(melbourneDate(project.updated_at))}</span>
        </div>
      </div>

      {/* "Copy delivery link" runs from the "···" sheet, which dismisses on
          select — so its result ("Copied!", or the failure) gets a small
          transient line above the tab bar instead of a row label nobody sees. */}
      {deliveryLinkNote && <div className="pd-toast">{deliveryLinkNote}</div>}

      {/* ── Sheets ─────────────────────────────────────────────────── */}
      <PageMenu open={menuOpen} onClose={() => setMenuOpen(false)} items={menuItems} />

      {fieldEdit && (
        <FieldSheet
          open
          title={fieldEdit.title}
          context={fieldEdit.context}
          kind={fieldEdit.kind}
          value={fieldEdit.value}
          placeholder={fieldEdit.placeholder}
          onSave={fieldEdit.onSave}
          onClose={() => setFieldEdit(null)}
        />
      )}
      {dateEdit && (
        <DateSheet
          open
          title={dateEdit.title}
          context={dateEdit.context}
          value={dateEdit.value}
          seed={dateEdit.seed}
          allowClear={dateEdit.allowClear}
          onPick={dateEdit.onPick}
          onClose={() => setDateEdit(null)}
        />
      )}

      {sub?.kind === 'phase-pick' && (
        <PickerSheet
          open
          title="Phase"
          items={PROJECT_PHASES.map((phase) => ({
            value: phase,
            label: PHASE_LABELS[phase],
            dotColor: PHASE_DOTS[phase],
          }))}
          value={project.phase}
          // PickerSheet dismisses itself after onSelect, so the close must not
          // clobber the confirm sheet onSelect just opened (decision 8).
          onClose={() => setSub((s) => (s?.kind === 'phase-pick' ? null : s))}
          onSelect={(phase) =>
            phase === project.phase ? closeSub() : pickPhase(phase)
          }
        />
      )}
      {sub?.kind === 'phase-confirm' && (
        <Sheet
          open
          onClose={closeSub}
          variant="half"
          title={`Move to ${PHASE_LABELS[sub.phase]}?`}
          context={phaseMoveArrow(project.phase, sub.phase)}
          footer={
            <>
              <button type="button" className="btn" onClick={closeSub}>
                Cancel
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={() => {
                  const next = sub.phase;
                  closeSub();
                  void patch({ phase: next });
                }}
              >
                Move to {PHASE_LABELS[sub.phase]}
              </button>
            </>
          }
        >
          <p className="pd-sheet-copy">{phaseMoveConsequence(project.phase, sub.phase)}</p>
        </Sheet>
      )}

      {sub?.kind === 'range' && (
        <RangeSheet
          which={sub.which}
          project={project}
          onClose={closeSub}
          openDate={openDate}
          patch={patch}
        />
      )}

      {sub?.kind === 'client' && (
        <PickerSheet
          open
          title="Client"
          searchable
          items={clients.map((client) => ({ value: client.id, label: client.name }))}
          value={project.client_id}
          onClose={closeSub}
          onSelect={(client_id) => {
            if (client_id !== project.client_id) void patch({ client_id });
          }}
        />
      )}
      {sub?.kind === 'contact' && (
        <PickerSheet
          open
          title="Contact"
          items={[
            ...clientContacts.map((c) => ({
              value: c.id,
              label: contactOptionLabel(c, c.is_default === 1),
              sub: c.email ?? undefined,
            })),
            { value: CUSTOM_CONTACT_VALUE, label: 'Custom…', sub: 'Type a name, email and phone' },
          ]}
          value={
            transientCustomContact
              ? CUSTOM_CONTACT_VALUE
              : (project.contact_id ??
                clientContacts.find((c) => c.is_default === 1)?.id ??
                undefined)
          }
          onClose={closeSub}
          onSelect={(value) => {
            const custom = value === CUSTOM_CONTACT_VALUE;
            setTransientCustomContact(custom);
            void patch({ contact_id: custom ? null : value });
          }}
        />
      )}
      {sub?.kind === 'social' && (
        <PickerSheet
          open
          title="Social status"
          items={SOCIAL_STATUSES.map((status) => ({
            value: status,
            label: SOCIAL_STATUS_LABELS[status],
          }))}
          value={project.social_status}
          onClose={closeSub}
          onSelect={(social_status) => void patch({ social_status: social_status as SocialStatus })}
        />
      )}
      {sub?.kind === 'gate' && (
        <PickerSheet
          open
          title="Gated by"
          items={[
            { value: 'none', label: 'No gate' },
            ...invoices.map((invoice) => ({
              value: invoice.id,
              label: `${formatInvoiceNumber(invoice.invoice_number)} · ${formatMoney(invoice.amount_cents)}`,
            })),
          ]}
          value={project.gating_invoice_id ?? 'none'}
          onClose={closeSub}
          onSelect={(value) => void patch({ gating_invoice_id: value === 'none' ? null : value })}
        />
      )}

      {sub?.kind === 'tool' &&
        (() => {
          const entry = TOOL_MAP.find((t) => t.tool === sub.tool);
          if (!entry) return null;
          const row = artifacts.find((a) => a.tool === entry.tool && (!sub.artifactId || a.id === sub.artifactId)) ?? null;
          const candidates = unlinked.filter((c) => c.tool === entry.tool);
          const canCreate =
            entry.tool === 'script' || entry.tool === 'callsheet' || entry.tool === 'review';
          return (
            <Sheet
              open
              onClose={closeSub}
              variant="half"
              title={entry.label}
              context={row ? (row.title ?? entry.label) : 'Not linked'}
              rightAction={<SheetButton onClick={closeSub}>Close</SheetButton>}
            >
              {canCreate && toolConfigured(entry.tool) && (
                <SheetRow
                  icon={<PlusIcon />}
                  label={`New ${entry.label.toLowerCase()}`}
                  onSelect={() => {
                    closeSub();
                    onCreateArtifact(entry.tool as 'script' | 'callsheet' | 'review');
                  }}
                />
              )}
              {entry.urlField !== null && (
                <SheetRow
                  icon={<LinkIcon />}
                  label={`Paste ${entry.label.toLowerCase()} URL`}
                  sub={project[entry.urlField] ?? undefined}
                  onSelect={() => {
                    closeSub();
                    openToolUrlSheet(entry.label, entry.urlField!);
                  }}
                />
              )}
              {candidates.length > 0 && <SheetSection>Found on the dashboard</SheetSection>}
              {candidates.map((candidate, i) => (
                <SheetRow
                  key={i}
                  icon={<CheckIcon />}
                  label={`Adopt “${candidate.title || 'Untitled'}”`}
                  onSelect={() => {
                    closeSub();
                    void adoptArtifact(candidate);
                  }}
                />
              ))}
              {row && (
                <SheetRow
                  icon={<XIcon />}
                  label="Unlink"
                  tone="danger"
                  onSelect={() => {
                    closeSub();
                    void unlinkArtifact(row.id);
                  }}
                />
              )}
            </Sheet>
          );
        })()}

      {sub?.kind === 'booking' && (
        <ProjectDetailBookingSheet
          booking={sub.booking}
          register={crewRegister}
          conflicts={sub.booking ? (crewConflicts[sub.booking.id] ?? []) : []}
          projectName={project.name}
          today={today}
          onSave={(fields) => patchCrewBooking(sub.booking!.id, fields)}
          onAdd={addCrewBooking}
          onRemove={() => removeCrewBooking(sub.booking!.id)}
          onClose={closeSub}
        />
      )}

      {sub?.kind === 'testimonial' && (
        <Sheet
          open
          onClose={closeSub}
          variant="half"
          title="Testimonial"
          context={`${project.name} · delivered ${formatDayMonth(project.delivered_date)}`}
          rightAction={<SheetButton onClick={closeSub}>Close</SheetButton>}
        >
          <div className="pd-sheet-copy">{testimonialSub(project)}</div>
          {project.testimonial_video_key && <p className="pd-sheet-copy">
            {testimonialPermissionLabel(project.testimonial_permission)}<br />
            <a href={testimonialMediaAdminLink(project.testimonial_video_key)} target="_blank" rel="noopener noreferrer">Play video</a>
          </p>}
          {project.testimonial_closed_at ? (
            <SheetRow
              icon={<CheckIcon />}
              label="Reopen the ask"
              onSelect={() => {
                closeSub();
                void testimonialAction('reopen');
              }}
            />
          ) : (
            <>
              <SheetRow
                icon={<CheckIcon />}
                label="Mark responded"
                onSelect={() => {
                  closeSub();
                  void testimonialAction('responded');
                }}
              />
              <SheetRow
                icon={<XIcon />}
                label="Don't ask"
                onSelect={() => {
                  closeSub();
                  void testimonialAction('skip');
                }}
              />
            </>
          )}
        </Sheet>
      )}

      {sub?.kind === 'template' && (
        <PickerSheet
          open
          title="Add tasks from template"
          items={templates.map((t) => ({
            value: t.id,
            label: t.name,
            sub: `${t.items.length} task${t.items.length === 1 ? '' : 's'}`,
          }))}
          onClose={closeSub}
          onSelect={(templateId) => void applyTemplate(templateId)}
        />
      )}

      {sub?.kind === 'trash' && (
        <Sheet
          open
          onClose={closeSub}
          variant="half"
          title="Move project to trash?"
          context={project.name}
          footer={
            <>
              <button type="button" className="btn" onClick={closeSub}>
                Cancel
              </button>
              <button
                type="button"
                className="btn danger"
                onClick={() => {
                  if (invoices.length > 0) setSub({ kind: 'trash-invoices' });
                  else {
                    closeSub();
                    void onTrashProject(false);
                  }
                }}
              >
                Move to trash
              </button>
            </>
          }
        >
          <p className="pd-sheet-copy">
            It leaves every list and lands in Settings → Trash, where you can restore it. Nothing is
            deleted for good.
          </p>
        </Sheet>
      )}
      {sub?.kind === 'trash-invoices' && (
        <Sheet
          open
          onClose={closeSub}
          variant="half"
          title={`Also trash ${invoices.length} linked invoice${invoices.length === 1 ? '' : 's'}?`}
          context={project.name}
        >
          <SheetRow
            icon={<TrashIcon />}
            label={`Project + invoice${invoices.length === 1 ? '' : 's'}`}
            tone="danger"
            onSelect={() => {
              closeSub();
              void onTrashProject(true);
            }}
          />
          <SheetRow
            icon={<TrashIcon />}
            label="Project only"
            onSelect={() => {
              closeSub();
              void onTrashProject(false);
            }}
          />
          <SheetRow icon={<XIcon />} label="Cancel" tone="dim" onSelect={closeSub} />
        </Sheet>
      )}
    </div>
  );
}
